import { React } from '@digsup/core-ui';
import PropTypes from 'prop-types';
import { Button } from '@digsup/components-button';
import { Select } from '@digsup/components-select';
import { Input } from '@digsup/components-input';
import { Card } from '@digsup/components-card';
import { Image } from '@digsup/components-image';
import { Text } from '@digsup/components-text';
import { List } from '@digsup/components-list';
import { Icon } from '@digsup/components-icon';
import { Form, Yup } from '@digsup/components-form';
import imgNoStreetView from '@digsup/assets-images/src/no-street-view.svg';
import './MultiunitProperty.scss';

export const MultiunitProperty = ({
  address,
  img,
  unit,
  unitType,
  multiunitTypeOptions,
  onClose,
  onSubmit,
}) => {
  const formSchema = Yup.object().shape({
    unitType: Yup.object().required('Required'),
    unit: Yup.string().required('Required'),
  });

  const selectedMultiUnitValue =
    Array.isArray(multiunitTypeOptions) &&
    multiunitTypeOptions?.find(option => option?.value === unitType);

  const initialValues = {
    unit,
    unitType: selectedMultiUnitValue,
  };

  return (
    <div className="c-map-multiunit-property u-display-flex@medium u-text-align-center">
      <Card className="c-map-multiunit-property__media u-display-inline-flex u-background-white u-margin-bottom-xlarge-stretched u-margin-bottom-none@medium u-margin-right-xlarge@medium">
        <Card.Body>
          <div className="u-margin-bottom-small">
            <Image
              ratio={179 / 154}
              src={img ? `${img}?w=246&h=211&fit=crop` : imgNoStreetView}
              srcset={
                img
                  ? `${img}?w=246&h=211&fit=crop 1x, ${img}?w=246&h=211&fit=crop 2x`
                  : null
              }
              radius="xsmall"
              alt="Property image"
              className="c-map-multiunit-property__img"
            />
          </div>
          <Text
            size="4xs"
            align="center"
            nowrap
            className="c-map-multiunit-property__title">
            {address}
          </Text>
        </Card.Body>
      </Card>
      <Card
        size="large"
        className="c-map-multiunit-property__content u-background-white">
        <Button
          onClick={onClose}
          size="small"
          variant="flat"
          iconOnly
          icon={<Icon name="x" size="small" />}
          className="c-map-multiunit-property__close-btn"
        />
        <Card.Body>
          <Text
            size="2xs"
            fontFamily="serif"
            fontWeight="semibold"
            color="theme-headline"
            align="center"
            marginBottom="small">
            Please specify your unit
          </Text>
          <Text color="theme-headline" align="center" marginBottom="small">
            For example, &apos;Floor 3&apos; or &apos;Apartment 203B&apos;
          </Text>
          <Form
            onSubmit={onSubmit}
            validationSchema={formSchema}
            initialValues={initialValues}>
            {() => (
              <>
                <div className="u-margin-bottom-small">
                  <List display="flex" nowrap spacing="small">
                    <List.Item className="u-flex-grow">
                      <Form.Field name="unitType">
                        {({ form, field }) => (
                          <Select
                            {...field}
                            onChange={option =>
                              form.setFieldValue(field.name, option)
                            }
                            options={multiunitTypeOptions}
                            size="medium"
                            placeholder="Select Unit Type"
                          />
                        )}
                      </Form.Field>
                    </List.Item>
                    <List.Item className="c-map-multiunit-property__unit-input u-flex-grow-none">
                      <Form.Field name="unit">
                        {({ field }) => <Input placeholder="Unit" {...field} />}
                      </Form.Field>
                    </List.Item>
                  </List>
                </div>
                <Button variant="primary" width="full" type="submit">
                  Confirm Address
                </Button>
              </>
            )}
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

MultiunitProperty.propTypes = {
  /**
   * Property address
   */
  address: PropTypes.string.isRequired,
  /**
   * Property image
   */
  img: PropTypes.string,
  /**
   * Multiunit type select options
   */
  multiunitTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  /**
   * Callback for info and placeholder cards close btn click
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Callback when user submits form
   */
  onSubmit: PropTypes.func.isRequired,
  unit: PropTypes.string,
  unitType: PropTypes.string,
};
